import React, { PropsWithChildren } from "react";

import { Navigate, useLocation } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { useAuth } from "@/hooks/useAuth";

import { projectKeys } from "@/constants/CQueryKeys";

import { TProject } from "@/types";

/**
 * A person with no project is sent to the onboarding - unless they are
 * already there. The view comes with the account (created at register),
 * but nothing can be logged until there is a project to log it on
 * (docs/modele.md, phase 6). A router redirect, not a hard navigation:
 * the query stays cached and there is nothing to reload.
 */
export const OnboardingGate: React.FC<PropsWithChildren> = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const { pathname } = useLocation();
	const { data, isFetched } = useQuery({
		queryKey: projectKeys.all,
		queryFn: (): Promise<TProject[]> => ProjectsService.list(),
		enabled: isAuthenticated,
		staleTime: 5 * 60 * 1000,
	});

	if (
		isFetched &&
		(data ?? []).length === 0 &&
		!pathname.startsWith("/onboarding")
	)
		return <Navigate to="/onboarding" replace />;

	return <>{children}</>;
};
